import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { ThumbnailCTRMetrics } from '../types/ThumbnailVariant';
import { Loader2, TrendingUp, MousePointer, Eye } from 'lucide-react';

interface ViewCTRDialogProps {
  open: boolean;
  onClose: () => void;
  showId: string;
  metadataType: string;
  containerIndex: number;
}

export function ViewCTRDialog({
  open,
  onClose,
  showId,
  metadataType,
  containerIndex,
}: ViewCTRDialogProps) {
  const [metrics, setMetrics] = useState<ThumbnailCTRMetrics | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      fetchCTRMetrics();
    }
  }, [open, showId, metadataType]);

  const fetchCTRMetrics = async () => {
    setLoading(true);
    setError(null);

    try {
      // TODO: Fix CORS issue with reels endpoint or use proper CMS endpoint
      // const allMetrics = await thumbnailCTRService.fetchCTRMetrics(showId, metadataType);
      // const metric = allMetrics.find(m => m.container_index === containerIndex);
      // if (metric) {
      //   setMetrics(metric);
      // } else {
      //   setError('No CTR data available for this variant yet.');
      // }

      setError('CTR metrics endpoint is being configured. Please check back later.');
    } catch (err) {
      console.error('Failed to fetch CTR metrics:', err);
      setError('Failed to load CTR metrics. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>CTR Metrics - Variant #{containerIndex}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {loading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          )}

          {error && (
            <div className="p-4 rounded-lg bg-destructive/10 text-destructive text-sm">
              {error}
            </div>
          )}

          {!loading && !error && metrics && (
            <div className="space-y-4">
              {/* Metrics Grid */}
              <div className="grid grid-cols-3 gap-4">
                {/* Impressions */}
                <div className="p-4 rounded-lg border bg-card space-y-2">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Eye className="h-4 w-4" />
                    <span className="text-xs font-medium">Impressions</span>
                  </div>
                  <div className="text-2xl font-bold">{metrics.impressions.toLocaleString()}</div>
                </div>

                {/* Clicks */}
                <div className="p-4 rounded-lg border bg-card space-y-2">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <MousePointer className="h-4 w-4" />
                    <span className="text-xs font-medium">Clicks</span>
                  </div>
                  <div className="text-2xl font-bold">{metrics.clicks.toLocaleString()}</div>
                </div>

                {/* CTR */}
                <div className="p-4 rounded-lg border bg-primary/10 space-y-2">
                  <div className="flex items-center gap-2 text-primary">
                    <TrendingUp className="h-4 w-4" />
                    <span className="text-xs font-medium">CTR</span>
                  </div>
                  <div className="text-2xl font-bold text-primary">{metrics.ctr.toFixed(2)}%</div>
                </div>
              </div>

              {/* Additional Info */}
              <div className="p-3 rounded-lg bg-muted/50 space-y-1 text-xs text-muted-foreground">
                <div className="flex justify-between">
                  <span>Last updated:</span>
                  <span className="font-medium">{formatDate(metrics.updated_at)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Tracking since:</span>
                  <span className="font-medium">{formatDate(metrics.created_at)}</span>
                </div>
              </div>

              {/* Info Note */}
              <div className="text-xs text-muted-foreground">
                <p>
                  📊 These metrics represent all-time performance for this thumbnail variant.
                  Data is updated daily.
                </p>
              </div>
            </div>
          )}

          {!loading && !error && !metrics && (
            <div className="text-center py-8 text-muted-foreground text-sm">
              No CTR data available yet. Data will appear once impressions are tracked.
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
